import {
  BadgeCheck,
  BriefcaseIcon,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Euro,
  FilePen,
  FileUp,
  FileWarning,
  Fingerprint,
  Globe,
  Hash,
  type LucideIcon,
  Mail,
  MapPin,
  PartyPopper,
  Phone,
  Scale,
  Search,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldCheckIcon,
  User,
} from 'lucide-react';
import {
  CustomFunnelAuth,
  type ConditionalVisibility,
  type FormSection,
  type FormSectionsWithShortcuts,
  type KorintModulesProductsConfig,
} from 'korint-sdk/react';
import { ContactKey } from '@folio/utils/customerDisplayName';
import {
  derogationControllerCreateDerogation,
  ManualQuoteComponentDto,
  ManualQuotePercentageDto,
  policyControllerPushPolicyEvent,
  PolicyDto,
} from 'korint-sdk';
import { centsToEuros, eurosToCents } from '@folio/utils';
import i18next from 'i18next';
import z, { ZodTypeAny } from 'zod';
import { IncludedPerilsCyber } from '../custom-pages/IncludedPerilsCyber';

const showWhenCompanyExists = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: false,
  },
] as const satisfies ConditionalVisibility[];

const showWhenCompanyBeingCreated = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: true,
  },
] as const satisfies ConditionalVisibility[];

const BILLING_FREQUENCY_ICONS = {
  MONTHLY: CalendarDays,
  QUARTERLY: CalendarRange,
  HALF_YEARLY: CalendarClock,
  YEARLY: CalendarCheck,
};

const killingQuestionBlockingValidation = {
  additionalValidationRules: (schema: ZodTypeAny) =>
    schema.and(
      z.literal('false', {
        errorMap: () => ({
          message: 'Il est impossible de souscrire une police dans ce cas',
        }),
      }),
    ),
};

const killingQuestionField = (name: string, icon: LucideIcon) => ({
  name,
  display: 'toggle' as const,
  icon,
  blockingValue: 'true',
  blockingErrorMessage: 'Il est impossible de souscrire une police dans ce cas',
  ...killingQuestionBlockingValidation,
});

// Cyber killing questions (feuille « Cyber » du fichier Killing Questions v0726) : bloquantes si « Oui »,
// regroupées par bloc/type. La KQ cyber-fraude n'est pas incluse : elle dépend de l'option cyber-fraude,
// choisie au devis, donc après ces questions de souscription.
const hiscoxHistorySection = {
  title: 'Questions de souscription - Historique Assurance Hiscox',
  fields: [killingQuestionField('kqHiscoxHistory', ShieldCheck)],
};

const activitySection = {
  title: 'Questions de souscription - Activité',
  fields: [
    killingQuestionField('kqActivity', BriefcaseIcon),
    killingQuestionField('kqFranchise', Building2),
    killingQuestionField('kqHolding', Euro),
    killingQuestionField('kqCyberHygiene', ShieldAlert),
  ],
};

const geographicExposureSection = {
  title: 'Questions de souscription - Exposition géographique',
  fields: [killingQuestionField('kqGeographicExposureCyber', MapPin)],
};

const claimsHistorySection = {
  title: 'Questions de souscription - Antécédents du risque',
  fields: [killingQuestionField('kqClaimsHistoryCyber', FileWarning)],
};

const policySections: FormSectionsWithShortcuts = [
  hiscoxHistorySection,
  activitySection,
  geographicExposureSection,
  claimsHistorySection,
];

// La KQ cyber-fraude porte sur l'option cyber-fraude, choisie au devis : elle est rendue sous la
// carte « Options complémentaires » (policySectionsAfterOptions) et n'apparaît que si le peril
// CYBER_FRAUDE est souscrit (showWhen lit le pseudo-champ peril-<ID> du form state).
const showWhenCyberFraudeSelected = [
  {
    fieldName: 'peril-CYBER_FRAUDE',
    condition: 'equals',
    value: 'true',
  },
] as const satisfies ConditionalVisibility[];

const cyberFraudeQuoteSections: FormSectionsWithShortcuts = [
  {
    title: 'Questions de souscription - Option cyber-fraude',
    showWhen: showWhenCyberFraudeSelected,
    fields: [killingQuestionField('kqCyberFraude', Fingerprint)],
  },
];

const signatorySections: FormSectionsWithShortcuts = [
  {
    title: 'Signataire',
    fields: [
      { name: 'contactCivility', icons: { MONSIEUR: User, MADAME: User } },
      { name: 'contactFirstName', colSpan: 6 as const, icon: User },
      { name: 'contactLastName', colSpan: 6 as const, icon: User },
      { name: 'contactEmail', colSpan: 6 as const, icon: Mail },
      { name: 'contactPhoneNumber', colSpan: 6 as const, icon: Phone },
      { name: 'contactBirthDate', colSpan: 4 as const, icon: CalendarDays },
      { name: 'contactBirthCity', colSpan: 4 as const, icon: MapPin },
      { name: 'contactBirthCountry', colSpan: 4 as const, icon: Globe },
    ],
  },
];

const companySections: FormSectionsWithShortcuts = [
  {
    title: "Informations de l'entreprise",
    fields: [
      { name: 'isCompanyBeingCreated', type: 'checkbox' },
      {
        name: 'companySiret',
        type: 'company',
        icon: Search,
        isCustomField: true,
        displayCompanyInformation: true,
        apiDataAccessor: apiData =>
          apiData.customFields?.companySiret
            ? {
                siret: apiData?.customFields?.companySiret,
                name: apiData?.customFields?.companyName,
                location: {
                  postCode: apiData?.customFields?.companyHeadquarterPostcode,
                  city: apiData?.customFields?.companyHeadquarterCity,
                  address: apiData?.customFields?.companyHeadquarterAddress,
                },
                nafCode: apiData?.customFields?.companyNafCode,
              }
            : null,
        formDataAccessor: 'companySiret.siret',
        showWhen: showWhenCompanyExists,
      },
      { name: 'companyName', icon: Building2, showWhen: showWhenCompanyBeingCreated },
      { name: 'companyLegalStatus1', icon: Scale, showWhen: showWhenCompanyBeingCreated },
      { name: 'companyCreationDate', icon: CalendarDays, showWhen: showWhenCompanyBeingCreated },
      {
        name: 'companyHeadquarterAddress',
        type: 'address',
        icon: MapPin,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterPostcode',
        colSpan: 4 as const,
        icon: Hash,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterCity',
        type: 'city',
        colSpan: 8 as const,
        icon: Building2,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterCountry',
        type: 'country',
        icon: Globe,
        showWhen: showWhenCompanyBeingCreated,
      },
      { name: 'companyNafCode', icon: Hash, showWhen: showWhenCompanyBeingCreated },
    ],
  },
  {
    title: "Caractéristiques de l'entreprise",
    fields: [
      { name: 'companyActivityLabel', searchable: true, icon: BriefcaseIcon },
      { name: 'companyRevenueOutsideUsaCanada', icon: Euro },
      { name: 'companyRevenueInUSACanada', icon: Euro },
      {
        name: 'totalCompanyRevenueCheck',
        type: 'computed',
        hidden: true,
        ignoreInPayload: true,
        dependsOn: ['companyRevenueOutsideUsaCanada', 'companyRevenueInUSACanada'],
        formDataAccessor: (data: Record<string, unknown>) =>
          (Number(data.companyRevenueOutsideUsaCanada) || 0) +
          (Number(data.companyRevenueInUSACanada) || 0),
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.number().refine(total => total <= 25_000_000, {
              message: i18next.t('tutassurHiscoxCyber.underwriting.maxRevenue'),
            }),
          ),
      },
      {
        name: 'usCanadaShareCheck',
        type: 'computed',
        hidden: true,
        ignoreInPayload: true,
        dependsOn: ['companyRevenueOutsideUsaCanada', 'companyRevenueInUSACanada'],
        formDataAccessor: (data: Record<string, unknown>) => ({
          inUSA: Number(data.companyRevenueInUSACanada) || 0,
          outside: Number(data.companyRevenueOutsideUsaCanada) || 0,
        }),
        additionalValidationRules: (schema: ZodTypeAny) =>
          schema.and(
            z.object({ inUSA: z.number(), outside: z.number() }).refine(
              ({ inUSA, outside }) => {
                const total = inUSA + outside;
                return total === 0 || inUSA / total < 0.25;
              },
              {
                message: i18next.t('tutassurHiscoxCyber.underwriting.usCanadaShareMax'),
              },
            ),
          ),
      },
    ],
  },
];

const componentIds = ['CYBER'];

const additionalQuoteComponentsSections = [
  {
    title: 'Frais grossistes',
    forTypes: ['WHOLESALE'],
    fields: [
      {
        name: 'wholesaleAdditionalCommissionPercentage',
        required: true,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const wholesaleAdditionalCommissionAmountWithFeesPreTaxes = data?.quote?.components?.find(
            c => c.id === 'WHOLESALE_ADDITIONAL_COMMISSION',
          )?.premium.amountWithFeesPreTaxes;

          const premiumAmountPreFeesAndTaxes = data?.quote?.components
            ?.filter(c => componentIds.includes(c.id))
            ?.reduce((sum, c) => sum + (c.premium.amountPreFeesAndTaxes ?? 0), 0);

          if (
            !wholesaleAdditionalCommissionAmountWithFeesPreTaxes ||
            !premiumAmountPreFeesAndTaxes ||
            premiumAmountPreFeesAndTaxes === 0
          )
            return 10;

          return Math.round(
            (wholesaleAdditionalCommissionAmountWithFeesPreTaxes / premiumAmountPreFeesAndTaxes) *
              100,
          );
        },
        formDataAccessor: (data: Record<string, unknown>) => ({
          wholesaleAdditionalCommissionComponent: {
            componentIds,
            id: 'WHOLESALE_ADDITIONAL_COMMISSION',
            type: 'FEE',
            chargeType: 'RECURRING',
            amountPropertiesFrom: ['amountPreFeesAndTaxes'],
            percentage: Number(data.wholesaleAdditionalCommissionPercentage) ?? 0,
          } satisfies ManualQuotePercentageDto,
        }),
      },
      {
        name: 'wholesaleSubscriptionFee',
        required: false,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const wholesaleSubscriptionFee = data?.quote?.components?.find(
            c => c.id === 'WHOLESALE_SUBSCRIPTION_FEE',
          );

          return centsToEuros(wholesaleSubscriptionFee?.premium?.amountWithFeesAndTaxes);
        },
        formDataAccessor: (data: Record<string, unknown>) => ({
          wholesaleSubscriptionFeeComponent: {
            id: 'WHOLESALE_SUBSCRIPTION_FEE',
            type: 'FEE',
            chargeType: 'ONE_TIME',
            premium: {
              amountPreFeesAndTaxes: 0,
              totalFees: eurosToCents(data.wholesaleSubscriptionFee as number | undefined) ?? 0,
              totalTaxes: 0,
            },
          } satisfies ManualQuoteComponentDto,
        }),
      },
    ],
  },
  {
    title: 'Frais distributeurs',
    forTypes: ['RETAIL'],
    fields: [
      {
        name: 'intermediaryCommission',
        required: false,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const intermediaryCommission = data?.quote?.components?.find(
            c => c.id === 'RETAIL_COMMISSION',
          );

          const premiumAmountPreFeesAndTaxes = data?.quote?.components
            ?.filter(c => componentIds.includes(c.id))
            ?.reduce((sum, c) => sum + (c.premium.amountPreFeesAndTaxes ?? 0), 0);

          if (
            !intermediaryCommission ||
            premiumAmountPreFeesAndTaxes === undefined ||
            premiumAmountPreFeesAndTaxes === 0
          )
            return undefined;

          return Math.round(
            (intermediaryCommission.premium.amountWithFeesPreTaxes / premiumAmountPreFeesAndTaxes) *
              100,
          );
        },
        formDataAccessor: data => ({
          intermediaryCommissionPercentage: {
            componentIds,
            id: 'RETAIL_COMMISSION',
            type: 'FEE',
            chargeType: 'RECURRING',
            amountPropertiesFrom: ['amountPreFeesAndTaxes'],
            percentage: (data.intermediaryCommission ?? 0) as number,
          } satisfies ManualQuotePercentageDto,
          wholesaleAdditionalCommissionComponent: {
            componentIds,
            id: 'WHOLESALE_ADDITIONAL_COMMISSION',
            type: 'FEE',
            chargeType: 'RECURRING',
            amountPropertiesFrom: ['amountPreFeesAndTaxes'],
            percentage: 10,
          } satisfies ManualQuotePercentageDto,
        }),
      },
      {
        name: 'retailAnnualFee',
        required: false,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const renewalFee = data?.quote?.components?.find(c => c.id === 'RETAIL_ANNUAL_FEE');
          return centsToEuros(renewalFee?.premium?.totalFees);
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const renewalFee = data.retailAnnualFee as number | undefined;
          if (!renewalFee) return {};
          return {
            renewalFeeComponent: {
              id: 'RETAIL_ANNUAL_FEE',
              type: 'FEE',
              chargeType: 'SPOT',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(renewalFee) ?? 0,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
      {
        name: 'retailSubscriptionFee',
        required: false,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const retailSubscriptionFee = data?.quote?.components?.find(
            c => c.id === 'RETAIL_SUBSCRIPTION_FEE',
          );

          return centsToEuros(retailSubscriptionFee?.premium?.amountWithFeesAndTaxes);
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const totalFees = data.retailSubscriptionFee as number | undefined;

          if (!totalFees) return {};

          return {
            intermediarySubscriptionFeeComponent: {
              id: 'RETAIL_SUBSCRIPTION_FEE',
              type: 'FEE',
              chargeType: 'ONE_TIME',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(totalFees) ?? 0,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
    ],
  },
  {
    // Same distributor fees, exposed to WHOLESALE brokers as well (PJ Pro parity).
    // Unlike the RETAIL section, the intermediary commission here does NOT auto-inject
    // a wholesale additional commission: a wholesale broker sets that explicitly in the
    // "Frais grossistes" section above, so injecting 10% here would overwrite it.
    title: 'Frais distributeurs',
    forTypes: ['WHOLESALE'],
    fields: [
      {
        name: 'intermediaryCommission',
        required: false,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const intermediaryCommission = data?.quote?.components?.find(
            c => c.id === 'RETAIL_COMMISSION',
          );

          const premiumAmountPreFeesAndTaxes = data?.quote?.components
            ?.filter(c => componentIds.includes(c.id))
            ?.reduce((sum, c) => sum + (c.premium.amountPreFeesAndTaxes ?? 0), 0);

          if (
            !intermediaryCommission ||
            premiumAmountPreFeesAndTaxes === undefined ||
            premiumAmountPreFeesAndTaxes === 0
          )
            return undefined;

          return Math.round(
            (intermediaryCommission.premium.amountWithFeesPreTaxes / premiumAmountPreFeesAndTaxes) *
              100,
          );
        },
        formDataAccessor: data => ({
          intermediaryCommissionPercentage: {
            componentIds,
            id: 'RETAIL_COMMISSION',
            type: 'FEE',
            chargeType: 'RECURRING',
            amountPropertiesFrom: ['amountPreFeesAndTaxes'],
            percentage: (data.intermediaryCommission ?? 0) as number,
          } satisfies ManualQuotePercentageDto,
        }),
      },
      {
        name: 'retailAnnualFee',
        required: false,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const renewalFee = data?.quote?.components?.find(c => c.id === 'RETAIL_ANNUAL_FEE');
          return centsToEuros(renewalFee?.premium?.totalFees);
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const renewalFee = data.retailAnnualFee as number | undefined;
          if (!renewalFee) return {};
          return {
            renewalFeeComponent: {
              id: 'RETAIL_ANNUAL_FEE',
              type: 'FEE',
              chargeType: 'SPOT',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(renewalFee) ?? 0,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
      {
        name: 'retailSubscriptionFee',
        required: false,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const retailSubscriptionFee = data?.quote?.components?.find(
            c => c.id === 'RETAIL_SUBSCRIPTION_FEE',
          );

          return centsToEuros(retailSubscriptionFee?.premium?.amountWithFeesAndTaxes);
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const totalFees = data.retailSubscriptionFee as number | undefined;

          if (!totalFees) return {};

          return {
            intermediarySubscriptionFeeComponent: {
              id: 'RETAIL_SUBSCRIPTION_FEE',
              type: 'FEE',
              chargeType: 'ONE_TIME',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(totalFees) ?? 0,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
    ],
  },
] as const satisfies Array<FormSection & { forTypes?: string[] }>;

const policyHolderInvitationRole = ['DRIVER'];

// ============================================================================
// Anonymous Funnel Steps
// ============================================================================
export const tutassurHiscoxCyberConfig = {
  display: {
    category: 'professionnel',
    icon: ShieldCheckIcon,
    badges: ['online'],
  },
  productCustomPath: '/cyber-tarif-en-ligne',
  discoveryFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'documents-upload',
        icon: FileUp,
        type: 'documentUploadFunnel' as const,
        context: 'policy' as const,
      },
      {
        path: 'asset',
        type: 'emptyAssetFunnel',
        hidden: true,
      },
      {
        path: 'customer',
        icon: Building2,
        type: 'customerFunnel',
        sections: companySections,
      },
      {
        path: 'contact',
        icon: User,
        type: 'customerFunnel',
        sections: signatorySections,
      },
      {
        path: 'compliance',
        icon: BadgeCheck,
        type: 'complianceFunnel',
      },
      {
        path: 'billing',
        icon: CalendarClock,
        type: 'billingSelectionFunnel',
        frequencyIcons: BILLING_FREQUENCY_ICONS,
      },
      {
        path: 'policy',
        icon: ShieldAlert,
        type: 'policyFunnel',
        sections: policySections,
      },
      {
        path: 'quote',
        icon: Shield,
        type: 'quoteFunnel',
        policySectionsAfterOptions: cyberFraudeQuoteSections,
        assetSections: [
          {
            title: "Date de prise d'effet de l'assurance",
            fields: [
              {
                name: 'startedAt',
                type: 'datetime',
                min: 'now',
              },
            ],
          },
        ],
        additionalQuoteComponentsSections,
      },
      {
        path: 'end',
        icon: PartyPopper,
        type: 'funnelEnd',
        id: 'new-business-end',
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
      },
    ],
  },
  mtaFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'new',
        icon: FilePen,
        type: 'newMTACreation',
      },
      {
        path: 'customer',
        icon: Building2,
        type: 'customerFunnel',
        sections: companySections,
      },
      {
        path: 'contact',
        icon: User,
        type: 'customerFunnel',
        sections: signatorySections,
      },
      {
        path: 'compliance',
        icon: BadgeCheck,
        type: 'complianceFunnel',
      },
      {
        path: 'policy',
        icon: ShieldAlert,
        type: 'policyFunnel',
        sections: policySections,
      },
      {
        path: 'quote',
        icon: Shield,
        type: 'quoteFunnel',
        policySectionsAfterOptions: cyberFraudeQuoteSections,
        assetSections: [],
      },
      {
        path: 'end',
        icon: PartyPopper,
        type: 'funnelEnd',
        id: 'mta-end',
        callback: {},
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
      },
    ],
  },
  customerInformations: {
    fieldsConfig: [
      { key: 'companyName', contactKey: ContactKey.COMPANY_NAME },
      'companyHeadquarterAddress',
      'companyHeadquarterPostcode',
      'companyHeadquarterCity',
      'companySiret',
      'companySiren',
      'companyNafCode',
      { key: 'companyActivityLabel', hideIfEmpty: true },
      { key: 'companyRevenueOutsideUsaCanada', hideIfEmpty: true },
      { key: 'companyRevenueInUSACanada', hideIfEmpty: true },
      { key: 'contactFirstName', contactKey: ContactKey.FIRST_NAME },
      { key: 'contactLastName', contactKey: ContactKey.LAST_NAME },
      'contactEmail',
      'contactPhoneNumber',
      'contactCivility',
      'contactBirthDate',
      'contactBirthCity',
      'contactBirthCountry',
    ],
  },
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/cyber-tarif-en-ligne/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery/cyber-tarif-en-ligne/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: true,
  },
  quoteTierSelectModuleConfig: {
    customIncludedPerils: IncludedPerilsCyber,
  },
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: true,
    isQuoteTierModuleVisible: true,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
    customModules: [],
  },
  policyholderInvitationModuleConfig: {
    relationTypes: policyHolderInvitationRole,
  },
  finalizeNewBusinessModuleConfig: {
    buttons: [
      {
        id: 'send-via-email',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          policyStatuses: ['POLICY_QUOTED'],
        },
      },
    ],
    rules: [
      {
        id: 'documents-validation',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ policy }) => policy.customFields.allClientDocumentsOk === true,
      },
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: ({ paymentMethods }) =>
          paymentMethods.some(({ paymentMethodType }) => paymentMethodType === 'sepa_debit'),
      },
    ],
  },
  blockOnZeroPremium: true,
  priceBreakdownConfig: {
    groups: [
      { lines: [{ key: 'TOTAL_PREMIUM', variant: 'dimmed' }] },
      { lines: [{ key: 'BROKERAGE_FEES' }] },
      { lines: [{ key: 'SUBSCRIPTION_FEE' }, { key: 'ANNUAL_FEE', tooltip: true }] },
    ],
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: false,
    enablePolicyComparison: true,
  },
  // ============================================================================
  // Anonymous Funnels (Policyholder Self-Service)
  // ============================================================================
  customFunnels: [
    {
      name: 'anonymous-nb',
      basePath: '/anonymous/:brokerageFirmId/cyber-tarif-en-ligne/:customerId/:policyId/:branchId?',
      auth: CustomFunnelAuth.ANONYMOUS,
      layout: 'none',
      steps: [
        {
          path: 'asset',
          type: 'emptyAssetFunnel',
          hidden: true,
        },
        {
          path: 'customer',
          type: 'customerFunnel',
          sections: companySections,
        },
        {
          path: 'compliance',
          type: 'complianceFunnel',
        },
        {
          path: 'policy',
          type: 'policyFunnel',
          sections: policySections,
        },
        {
          path: 'quote',
          type: 'quoteFunnel',
          policySectionsAfterOptions: cyberFraudeQuoteSections,
          assetSections: [
            {
              title: "Date de prise d'effet de l'assurance",
              fields: [{ name: 'startedAt', type: 'datetime', min: 'now' }],
            },
          ],
        },
        {
          path: 'contact',
          type: 'customerFunnel',
          sections: signatorySections,
          maxCustomers: 1,
          minCustomers: 1,
        },
        {
          path: 'billing',
          type: 'billingSelectionFunnel',
          icon: CalendarClock,
          frequencyIcons: BILLING_FREQUENCY_ICONS,
        },
        {
          path: 'end',
          type: 'funnelEnd' as const,
          id: 'anonymous-nb-end',
          callback: {
            callToActionCallback: async ({
              policyId,
              branchId,
            }: {
              policyId: string;
              branchId: string;
            }) => {
              await policyControllerPushPolicyEvent(
                policyId,
                { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
                { branchId },
              );
              await derogationControllerCreateDerogation(branchId, { action: 'CONFIRM' });
            },
          },
          redirectTo: ({ policyId, branchId }: { policyId: string; branchId: string }) =>
            `/auth?redirect=${encodeURIComponent(`/policyholder/${policyId}/${branchId}`)}`,
          additionalComponents: {
            isQuoteTierModuleVisible: false,
            finishWithoutSendingButton: {
              enabled: false,
            },
          },
        },
      ],
    },
  ],
} satisfies KorintModulesProductsConfig;
